import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { I18NextPipe } from 'angular-i18next';
import { FilterMatchMode } from 'primeng/api';
import { Table } from 'primeng/table';
import { MxBaseComponent } from 'src/app/core/base/mx.base-component';
import {
  MxBrowserStorageService,
  MxConstans,
  MxConstansService,
  MxStorageType,
} from 'src/app/core/services';
import {
  MxTableColumn,
  MxTableOperationItems,
  MxTableOptions,
} from 'src/app/shared/domains/common/interfaces';
import { MxColumnType } from 'src/app/shared/enums';
import { MxDateFormatPipe, MxEnumAsStringPipe } from 'src/app/shared/pipes';

@Component({ standalone: false,
  selector: 'mx-scroll-table[tableOptions]',
  templateUrl: './mx.scroll-table.component.html',
  styleUrls: ['./mx.scroll-table.component.scss'],
})
export class MxScrollTableComponent
  extends MxBaseComponent
  implements OnInit, OnChanges
{
  @ViewChild('table') table: Table;

  @Input() datasource: any[];
  @Input() tableOptions: MxTableOptions;
  @Input() defaultSortField: string = 'id';
  @Input() defaultSortOrder: number = -1;
  @Input() columnDefaultWidth: number = 200;
  @Input() rows: number = 200;
  @Input() filterDelay: number = 0;
  @Input() operationItems: MxTableOperationItems[];
  @Input() globalSearchVisible: boolean = true;
  @Input() rowHeight: number = 40;
  @Input() refreshTableVisible: boolean = true;

  @Output() datasourceChange: EventEmitter<any[]> = new EventEmitter<any[]>();
  @Output() refreshData: EventEmitter<any> = new EventEmitter<any>();
  @Output() selectedDataChange: EventEmitter<any> = new EventEmitter<any>();

  statekey: string;

  title: string;
  firstCall: boolean = true;
  globalSearchValue: string;
  selectedColumns: MxTableColumn[];
  selectedData: any;

  constructor(
    private route: ActivatedRoute,
    private mxDateFormatPipe: MxDateFormatPipe,
    private i18nextPipe: I18NextPipe,
    private mxEnumAsStringPipe: MxEnumAsStringPipe
  ) {
    super();
    this.title = this.route.snapshot.data['title'];
  }

  ngOnInit() {
    this.statekey = `${MxConstansService.get(
      MxConstans.LOCAL_STORAGE_PREFIX
    )}-${this.tableOptions.localStorageKey}`;

    if (
      !MxBrowserStorageService.get(
        MxStorageType.Local,
        this.tableOptions.localStorageKey
      )
    ) {
      MxBrowserStorageService.set(
        MxStorageType.Local,
        this.tableOptions.localStorageKey,
        {
          sortField: this.defaultSortField,
          sortOrder: this.defaultSortOrder,
        }
      );
    } else {
      const storageKey = MxBrowserStorageService.get(
        MxStorageType.Local,
        this.tableOptions.localStorageKey
      );

      if (storageKey.selection) {
        delete storageKey.selection;

        MxBrowserStorageService.set(
          MxStorageType.Local,
          this.tableOptions.localStorageKey,
          storageKey
        );

        MxBrowserStorageService.set(
          MxStorageType.Local,
          this.tableOptions.localStorageKey,
          {
            sortField: this.defaultSortField,
            sortOrder: this.defaultSortOrder,
          }
        );
      }
    }

    const tableLocalStorageOptions = MxBrowserStorageService.get(
      MxStorageType.Local,
      this.tableOptions.localStorageKey
    );

    if (tableLocalStorageOptions?.filters?.global) {
      this.globalSearchValue = tableLocalStorageOptions?.filters?.global.value;
    }

    this.selectedColumns = this.tableOptions.columns.filter(
      (f) => f.defaultVisible === true
    );

    const localStorageColumns = MxBrowserStorageService.get(
      MxStorageType.Local,
      this.tableOptions.localStorageKey
    ).columnOrder;

    if (localStorageColumns) {
      const columnDiff = [
        ...this.getDifference(
          this.selectedColumns.map((m) => m.field),
          localStorageColumns
        ),
        ...this.getDifference(
          localStorageColumns,
          this.selectedColumns.map((m) => m.field)
        ),
      ];

      if (columnDiff.length !== 0) {
        MxBrowserStorageService.remove(
          MxStorageType.Local,
          this.tableOptions.localStorageKey
        );
        location.reload();
      }
    }
  }

  getDifference<T>(a: T[], b: T[]): T[] {
    return a.filter((element) => {
      return !b.includes(element);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.datasource) {
      if (changes.datasource.currentValue) {
        this.datasource.forEach((data) => {
          this.tableOptions.columns
            .filter((f) => f.defaultVisible)
            .forEach((f) => {
              if (f.type === MxColumnType.Boolean) {
                data[`${f.field}_`] = data[`${f.field}`]
                  ? this.i18nextPipe.transform('Yes')
                  : this.i18nextPipe.transform('No');

                this.addPrettyFieldToGlobalFilterFields(f.field);
              } else if (f.type === MxColumnType.Enum) {
                data[`${f.field}_`] = this.i18nextPipe.transform(
                  this.mxEnumAsStringPipe.transform(
                    data[`${f.field}`],
                    f.enum,
                    f.enumName
                  )
                );

                this.addPrettyFieldToGlobalFilterFields(f.field);
              } else if (f.type === MxColumnType.Number && f.isPercentage) {
                data[`${f.field}_`] = data[`${f.field}`] * 100;
              }
              else if (f.type === MxColumnType.Number && f.suffix.includes('Ft')) {
                data[`${f.field}_`] = data[`${f.field}`].toLocaleString("hu-HU")
              }

              else if (
                f.type === MxColumnType.Date ||
                f.type === MxColumnType.DateTime
              ) {
                data[`${f.field}_`] =
                  data[`${f.field}`] === null ||
                  data[`${f.field}`] === '' ||
                  data[`${f.field}`] === undefined
                    ? ''
                    : this.mxDateFormatPipe.transform(
                        data[`${f.field}`],
                        f.type === MxColumnType.Date
                          ? 'YYYY.MM.DD.'
                          : 'YYYY.MM.DD. HH:mm'
                      );

                this.addPrettyFieldToGlobalFilterFields(f.field);
              }

              if (f.prefix) {
                if (data[`${f.field}_`]) {
                  data[`${f.field}_`] = `${f.prefix}${data[`${f.field}_`]}`;
                } else {
                  data[`${f.field}_`] = `${f.prefix}${data[`${f.field}`]}`;
                }
                this.addPrettyFieldToGlobalFilterFields(f.field);
              }

              if (f.suffix) {
                if (data[`${f.field}_`]) {
                  data[`${f.field}_`] = `${data[`${f.field}_`]}${f.suffix}`;
                } else {
                  data[`${f.field}_`] = `${data[`${f.field}`]}${f.suffix}`;
                }
                this.addPrettyFieldToGlobalFilterFields(f.field);
              }
            });
        });

        // this.datasource = this.datasource.map((m) => {
        //   let ret = { ...m };
        //   this.tableOptions.columns
        //     .filter((f) => f.defaultVisible)
        //     .forEach((f) => {
        //       if (f.type === MxColumnType.Boolean) {
        //         ret[`${f.field}_`] = ret[`${f.field}`]
        //           ? this.i18nextPipe.transform('Yes')
        //           : this.i18nextPipe.transform('No');

        //         this.addPrettyFieldToGlobalFilterFields(f.field);
        //       } else if (f.type === MxColumnType.Enum) {
        //         ret[`${f.field}_`] = this.i18nextPipe.transform(
        //           this.mxEnumAsStringPipe.transform(
        //             ret[`${f.field}`],
        //             f.enum,
        //             f.enumName
        //           )
        //         );

        //         this.addPrettyFieldToGlobalFilterFields(f.field);
        //       } else if (f.type === MxColumnType.Number && f.isPercentage) {
        //         ret[`${f.field}_`] = ret[`${f.field}`] * 100;
        //       } else if (
        //         f.type === MxColumnType.Date ||
        //         f.type === MxColumnType.DateTime
        //       ) {
        //         ret[`${f.field}_`] =
        //           ret[`${f.field}`] === null ||
        //           ret[`${f.field}`] === '' ||
        //           ret[`${f.field}`] === undefined
        //             ? ''
        //             : this.mxDateFormatPipe.transform(
        //                 ret[`${f.field}`],
        //                 f.type === MxColumnType.Date
        //                   ? 'YYYY.MM.DD.'
        //                   : 'YYYY.MM.DD. HH:mm'
        //               );

        //         this.addPrettyFieldToGlobalFilterFields(f.field);
        //       }

        //       if (f.prefix) {
        //         if (ret[`${f.field}_`]) {
        //           ret[`${f.field}_`] = `${f.prefix}${ret[`${f.field}_`]}`;
        //         } else {
        //           ret[`${f.field}_`] = `${f.prefix}${ret[`${f.field}`]}`;
        //         }
        //         this.addPrettyFieldToGlobalFilterFields(f.field);
        //       }

        //       if (f.suffix) {
        //         if (ret[`${f.field}_`]) {
        //           ret[`${f.field}_`] = `${ret[`${f.field}_`]}${f.suffix}`;
        //         } else {
        //           ret[`${f.field}_`] = `${ret[`${f.field}`]}${f.suffix}`;
        //         }
        //         this.addPrettyFieldToGlobalFilterFields(f.field);
        //       }
        //     });

        //   return ret;
        // });
      }
    }
  }

  addPrettyFieldToGlobalFilterFields(field: string) {
    if (
      this.tableOptions.globalFilterFields.filter((gf) => gf === `${field}_`)
        .length === 0
    ) {
      this.tableOptions.globalFilterFields.push(`${field}_`);
    }
  }

  onGlobalSearchValueChange(event: any) {
    this.table.filterGlobal(event, FilterMatchMode.CONTAINS);
    this.datasourceChange.emit();
  }

  onRowSelect(event:any){
    this.selectedDataChange.emit(event.data);
  }
  onRowUnselect(event:any){
    this.selectedDataChange.emit(null);
  }
}
